import { useEffect, useState } from 'react';
import {
    collection,
    onSnapshot,
    orderBy,
    query,
    where,
} from 'firebase/firestore';

import { db } from '../../providers/firebase/firebase';
import type { IrmDataProvider } from '../../providers/types';
import { newKey } from './apiKeys';

export interface KeyRecord {
    id: string;
    prefix: string;
    label: string;
    uid: string;
    status: string;
    created_at?: string;
    last_used_at?: string;
}

interface UseUserApiKeysResult {
    keys: KeyRecord[];
    loading: boolean;
    /** True when the listen failed — a rules denial reads differently from
     *  "this user holds no keys", and the UI must not conflate them. */
    error: boolean;
    /** The Firestore error code behind `error`, or null. Worth surfacing:
     *  the two likely failures have different fixes and are otherwise
     *  indistinguishable in the UI. `permission-denied` means firestore.rules
     *  has not been deployed (api_keys matches no rule, and Firestore denies
     *  any unmatched path); `failed-precondition` means the composite index
     *  on (uid ASC, created_at DESC) is missing. Both are deploy state, not
     *  bugs in this component. */
    errorCode: string | null;
}

/**
 * Live stream of one user's API keys, newest first.
 *
 * This talks to Firestore directly rather than through the react-admin data
 * provider, following the gates hooks. react-admin-firebase translates a
 * single string filter into a *range* scan (`uid >= x` && `uid < x+'z'`),
 * which Firestore rejects outright when the sort is on another field:
 *
 *   "You have a where filter with an inequality on field 'uid' and so you must
 *    also use 'uid' as your first argument to orderBy()"
 *
 * Needs the composite index on (uid ASC, created_at DESC) in firestore.indexes.json.
 */
export function useUserApiKeys(uid: string): UseUserApiKeysResult {
    const [keys, setKeys] = useState<KeyRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [errorCode, setErrorCode] = useState<string | null>(null);

    useEffect(() => {
        if (!uid) {
            setKeys([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(false);
        setErrorCode(null);
        const q = query(
            collection(db, 'api_keys'),
            where('uid', '==', uid),
            orderBy('created_at', 'desc')
        );
        const unsub = onSnapshot(
            q,
            snap => {
                setKeys(
                    // Document id last: a stored `id` field must not shadow it.
                    // This id is what revokeApiKey() addresses, so a shadowed
                    // one would revoke the wrong key.
                    snap.docs.map(d => ({ ...d.data(), id: d.id }) as KeyRecord)
                );
                setLoading(false);
            },
            err => {
                setError(true);
                setErrorCode(err?.code ?? null);
                setLoading(false);
            }
        );
        return unsub;
    }, [uid]);

    return { keys, loading, error, errorCode };
}

/**
 * Revoke a key through adminRevokeApiKey.
 *
 * Server-side so that `revoked_by` comes from the verified token and the
 * revocation lands in `admin_audit` — a client write could record neither.
 * Revoking sets `status: 'revoked'` rather than deleting: the indexer treats
 * anything that is not `active` as a rejection, and it takes up to 60 seconds
 * to bite, the key cache TTL in api/partner/keys.py.
 */
export function revokeApiKey(
    dataProvider: IrmDataProvider,
    id: string
): Promise<{ ok: true; already_revoked: boolean }> {
    return dataProvider.adminRevokeApiKey(id);
}

/**
 * Mint one key.
 *
 * The raw key is generated and hashed HERE and never leaves the browser — the
 * callable receives only the SHA-256 and the log-safe prefix, so the secret
 * never crosses the network and cannot appear in a function log. The server
 * owns the authorization half: it reads status and features off the user
 * document rather than trusting this caller, and writes the audit entry.
 *
 * Returns the raw key, which is shown once and then unrecoverable.
 */
export async function createApiKey(
    dataProvider: IrmDataProvider,
    args: { uid: string; label: string }
): Promise<string> {
    const { raw, key_hash, prefix } = await newKey();
    await dataProvider.adminCreateApiKey({
        uid: args.uid,
        key_hash,
        prefix,
        label: args.label,
    });
    return raw;
}
